import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { RuntimeConfigService } from './runtime-config.service';
import { getApiUrl } from '../config/config-helpers';
import { AuthService } from './auth.service';

export const BUG_REPORT_TYPES = ['ui', 'gameplay', 'connection', 'performance', 'data', 'other'] as const;
export type BugReportType = typeof BUG_REPORT_TYPES[number];

export interface BugReportClientContext {
  pageUrl: string;
  userAgent: string;
  screenSize: string;
  timestamp: string;
}

export interface CreateBugReportRequest {
  bugType: BugReportType;
  description: string;
  contactMe: boolean;
  clientContext: BugReportClientContext;
}

export interface BugReportResponse {
  bugReportId: string;
}

@Injectable({
  providedIn: 'root'
})
export class BugReportService {
  private http = inject(HttpClient);
  private runtimeConfig = inject(RuntimeConfigService);
  private authService = inject(AuthService);
  private hasBugReportSubject = new BehaviorSubject<boolean>(false);
  readonly hasBugReport$ = this.hasBugReportSubject.asObservable();

  constructor() {
    this.authService.currentUser$.subscribe(user => {
      this.hasBugReportSubject.next(!!user?.hasBugReport);
    });
  }

  private get apiUrl(): string {
    return getApiUrl(this.runtimeConfig);
  }

  submitReport(
    bugType: BugReportType,
    description: string,
    contactMe: boolean
  ): Observable<BugReportResponse> {
    const payload: CreateBugReportRequest = {
      bugType,
      description,
      contactMe,
      clientContext: this.buildClientContext()
    };

    return this.http.post<BugReportResponse>(`${this.apiUrl}/bug-reports`, payload).pipe(
      tap(() => this.hasBugReportSubject.next(true))
    );
  }

  private buildClientContext(): BugReportClientContext {
    return {
      pageUrl: this.getSanitizedPageUrl(),
      userAgent: navigator.userAgent,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString()
    };
  }

  private getSanitizedPageUrl(): string {
    try {
      const url = new URL(window.location.href);
      return `${url.origin}${url.pathname}`;
    } catch {
      return window.location.origin;
    }
  }
}
